import {
  eyeIcon,
  loginIcon,
  DeviceDesktopIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import DropdownMenuItemContentRadio from "../../packages/excalidraw/components/dropdownMenu/DropdownMenuItemContentRadio";

import { isExcalidrawPlusSignedUser } from "../app_constants";
import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";

import type { DevicePreviewMode } from "../useHandleDevicePreview";

const PLUS_APP_URL = import.meta.env.VITE_APP_PLUS_APP;

const devicePreviewIconProps = {
  width: 24,
  height: 24,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const DevicePhoneIcon = (
  <svg {...devicePreviewIconProps} viewBox="0 0 24 24">
    <rect x="6.5" y="2.5" width="11" height="19" rx="2" />
    <path d="M10 19.5h4" />
  </svg>
);

const DeviceTabletIcon = (
  <svg {...devicePreviewIconProps} viewBox="0 0 24 24">
    <rect x="4" y="2.5" width="16" height="19" rx="2" />
    <path d="M10 19.5h4" />
  </svg>
);

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
  devicePreviewMode: DevicePreviewMode;
  onDevicePreviewModeChange: (mode: DevicePreviewMode) => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {isDevEnv() && props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      {/* Internal developer affordances stay hidden in production. */}
      {isDevEnv() && (
        <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      )}
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      {isDevEnv() && (
        <MainMenu.ItemLink
          icon={loginIcon}
          href={`${PLUS_APP_URL}${
            isExcalidrawPlusSignedUser ? "" : "/sign-up"
          }?utm_source=signin&utm_medium=app&utm_content=hamburger`}
          className="highlighted"
        >
          {isExcalidrawPlusSignedUser ? "Sign in" : "Sign up"}
        </MainMenu.ItemLink>
      )}
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      {/* Keep the advanced editor controls out of the shipped menu. */}
      {isDevEnv() && (
        <>
          <MainMenu.DefaultItems.Preferences />
          <MainMenu.DefaultItems.ExtraTools />
        </>
      )}
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      {isDevEnv() && (
        <DropdownMenuItemContentRadio
          name="devicePreviewMode"
          value={props.devicePreviewMode}
          onChange={(value) =>
            props.onDevicePreviewModeChange(value as DevicePreviewMode)
          }
          choices={[
            {
              value: "desktop",
              label: DeviceDesktopIcon,
              ariaLabel: "Computador",
            },
            {
              value: "tablet",
              label: DeviceTabletIcon,
              ariaLabel: "Tablet",
            },
            {
              value: "phone",
              label: DevicePhoneIcon,
              ariaLabel: "Celular",
            },
          ]}
        >
          Visual da tela
        </DropdownMenuItemContentRadio>
      )}
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
